/**
 * FormulaGroupCard — single formula group shell.
 *
 * Stable layout: collapsing uses an animated `grid-template-rows: 0fr / 1fr`
 * trick instead of `display: hidden`, so neighbours never flash or reflow.
 * The body is extracted to {@link FormulaGroupCardBody} to keep this file
 * under the 150-line cap.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { ALL_LAYERS } from '@trafficpulse/shared';
import type {
  FlowLayer,
  FormulaBlocksBody,
  FormulaGroup,
  FormulaGroupMode,
  FormulaSlidersBody,
} from '@trafficpulse/shared';

import { Card, CardContent } from '@/components/ui/layout/Card';
import { Button } from '@/components/ui/layout/Button';

import { GroupHeaderRow, LayerWeightsRow, astToSliders, slidersToAst } from './group';
import { recomputeGroupExpression, switchModeBody } from './formula-group.utils';
import { groupLayers } from './per-layer-weights.utils';
import FormulaGroupCardBody from './FormulaGroupCardBody';
import { useOptionalFlowOptimizerLivePreview } from '../../context/FlowOptimizerLivePreviewContext';
import { IgnorePathsSelect, ignorePathOptions } from '../../ignore-paths';
import { CopyGroupButton } from '../../clipboard';

export interface FormulaGroupCardProps {
  group: FormulaGroup;
  /** Weight per layer the group applies to (each layer sums to 100 globally). */
  weights: Partial<Record<FlowLayer, number>>;
  onChange: (next: FormulaGroup) => void;
  onWeightChange: (layer: FlowLayer, next: number) => void;
  onAppliesToChange: (next: ReadonlyArray<FlowLayer>) => void;
  onRemove: () => void;
}

const FormulaGroupCard: FC<FormulaGroupCardProps> = ({
  group,
  weights,
  onChange,
  onWeightChange,
  onAppliesToChange,
  onRemove,
}) => {
  const preview = useOptionalFlowOptimizerLivePreview();
  const pathOptions = useMemo(
    () => ignorePathOptions(preview?.result?.items),
    [preview?.result],
  );
  const [expanded, setExpanded] = useState(true);
  const expression = useMemo(() => recomputeGroupExpression(group), [group]);
  const update = (patch: Partial<FormulaGroup>) =>
    onChange({
      ...group,
      ...patch,
      expression: recomputeGroupExpression({ ...group, ...patch }),
    });
  const switchMode = (next: FormulaGroupMode) =>
    update(switchModeBody(group, next, slidersToAst, astToSliders));
  const setSliders = (body: FormulaSlidersBody) => update({ sliders: body });
  const setBlocks = (body: FormulaBlocksBody) => update({ blocks: body });
  const bodyId = `formula-group-editor-${group.id}`;
  const appliesTo: ReadonlyArray<FlowLayer> = group.appliesTo ?? ALL_LAYERS;
  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-3 p-3">
        <div className="flex items-start gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            aria-expanded={expanded}
            aria-controls={bodyId}
            title={expanded ? 'Collapse formula' : 'Expand formula'}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3" aria-hidden />
            ) : (
              <ChevronRight className="h-3 w-3" aria-hidden />
            )}
          </Button>
          <div className="min-w-0 flex-1">
            <GroupHeaderRow
              name={group.name}
              dateRange={group.dateRange}
              dateRangeCustomFrom={group.dateRangeCustomFrom}
              dateRangeCustomTo={group.dateRangeCustomTo}
              mode={group.mode}
              appliesTo={appliesTo}
              onNameChange={(next) => update({ name: next })}
              onDateRangeCommit={(patch) => update(patch)}
              onModeChange={switchMode}
              onAppliesToChange={onAppliesToChange}
              scopeControl={
                groupLayers(group).includes('paths') ? (
                  <IgnorePathsSelect
                    kind="formula"
                    value={group.ignoredPathBankIds ?? []}
                    options={pathOptions}
                    optionsReady={Boolean(preview?.result)}
                    onChange={(next) => update({ ignoredPathBankIds: next })}
                  />
                ) : undefined
              }
              onRemove={onRemove}
              trailingActions={<CopyGroupButton group={group} />}
            />
          </div>
        </div>
        <LayerWeightsRow
          layers={groupLayers(group)}
          weights={weights}
          onChange={onWeightChange}
        />
        <FormulaGroupCardBody
          id={bodyId}
          expanded={expanded}
          group={group}
          expression={expression}
          onSlidersChange={setSliders}
          onBlocksChange={setBlocks}
        />
      </CardContent>
    </Card>
  );
};

export default FormulaGroupCard;
